import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Hero.css";
import { searchDoctors } from "../../services/doctorService";
import HeroNavLink from "./HeroNavLink";
import { useTranslation } from "react-i18next";
import { getCurrentLang, switchLanguage } from "../../store/aboutStore"; // adjust the path if needed
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Doctor {
  id: number;
  name: string;
}

const Hero: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLang());
  const navigate = useNavigate();
  const isRTL = currentLang === "ar";
  

  useEffect(() => {
  const lang = getCurrentLang();
  setCurrentLang(lang);
  i18n.changeLanguage(lang);
  document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
}, []);

  useEffect(() => {
    document.documentElement.setAttribute(
      "dir",
      currentLang === "ar" ? "rtl" : "ltr"
    );
  }, [currentLang]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        setIsLoading(true);
        searchDoctors(searchTerm)
          .then((doctors) => {
            setSuggestions(Array.isArray(doctors) ? doctors : []);
          })
          .catch(() => setSuggestions([]))
          .finally(() => setIsLoading(false));
      } else {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const toggleLanguage = () => {
    const newLang = currentLang === "fr" ? "ar" : "fr";
    setCurrentLang(newLang);
    switchLanguage(newLang);
    setIsMenuOpen(false);
  };

  return (
    <section className="hero">
      <div className="hero-background">
        <img
          src="/images/bg.jpg"
          alt={t("hero_background_alt")}
          className="hero-bg-image"
        />
        <div className="hero-overlay"></div>
      </div>

      {/* Header */}
      <div className="hero-header">
        <div className="container">
          <Link to="/" className="logo">
            <img src="/images/logo.png" alt="Logo" />
          </Link>
          <button
            className={`mobile-menu-btn ${isMenuOpen ? "active" : ""}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
          <nav className={`nav ${isMenuOpen ? "open" : ""}`}>
            <div className="nav-left"></div>
            <div className="nav-right">
              <Link to="/professionnel-sante" className="hero-nav-link">
                {t("professional_healthcare")}
              </Link>
              <Link to="https://wa.me/+22227707210" className="hero-nav-link">
                {t("help")}
              </Link>
              <HeroNavLink />
              <div className={`language-toggle ${isRTL ? "rtl" : "ltr"}`}>
                {!isRTL && (
                  <FontAwesomeIcon icon={faGlobe} className="globe-icon" />
                )}
                <button
                  onClick={toggleLanguage}
                  className="language-button gr-title-2"
                  aria-label="Toggle language"
                  dir={isRTL ? "rtl" : "ltr"}
                >
                  {currentLang === "fr" ?  "العربية":"Français" }
                </button>
                {isRTL && (
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="globe-icon gr-title-2"
                  />
                )}
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Hero Content */}
      <div className="hero-content">
        <div className="container">
          <h1 className="gr-name">{t("hero_header")}</h1>
          <p style={{ marginBottom: "2rem" }}>{t("download_app")}</p>
          <div
            className="app-stores"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <a
              href="https://apps.apple.com/us/app/yata-m%C3%A9dical/id6615080510"
              target="_blank"
              rel="noopener noreferrer"
              className="store-button"
            >
              <img src="images/googlePlay.png" alt={t("download_app_store")} />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.yatamedical.YataPatient"
              target="_blank"
              rel="noopener noreferrer"
              className="store-button"
            >
              <img src="images/playStore.png" alt={t("download_google_play")} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;